import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IconName, IgntrIconComponent } from 'app/shared/igntr';
import { GamificationService } from 'app/api/services/gamification.service';

type ToastKind = 'points' | 'achievement' | 'challenge';

interface GamiToast {
  id: number;
  kind: ToastKind;
  icon: IconName;
  name?: string;
  reason?: string | null;
  points?: number;
  leveledUp?: boolean;
  levelText?: string;
  leaving?: boolean;
}

/**
 * App-wide celebratory toast for gamification rewards. Mounted once in the home
 * shell (apphomelayout) so it appears no matter which page the user is on.
 * Listens to the SignalR-fed subjects on GamificationService.
 */
@UntilDestroy()
@Component({
  selector: 'app-gamification-toast',
  standalone: true,
  imports: [CommonModule, TranslocoModule, IgntrIconComponent],
  templateUrl: './gamification-toast.component.html',
  styleUrls: ['./gamification-toast.component.scss'],
})
export class GamificationToastComponent implements OnInit {
  toasts: GamiToast[] = [];

  private seq = 0;
  private readonly DURATION = 6000;

  constructor(
    private gamification: GamificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.gamification.updates$
      .pipe(untilDestroyed(this))
      .subscribe((u) => {
        if (!u) return;
        const showForPoints = u.pointsAwarded > 0 && !!u.reason;
        // Reward-only points (no rule reason) are already covered by the
        // achievement/challenge toast — only surface a points toast for a real
        // rule award, or whenever the user levels up.
        if (!showForPoints && !u.leveledUp) return;

        this.push({
          kind: 'points',
          icon: 'star',
          points: u.pointsAwarded,
          reason: u.reason,
          leveledUp: u.leveledUp,
          levelText: u.leveledUp
            ? u.levelTitle
              ? `${u.level} · ${u.levelTitle}`
              : `${u.level}`
            : undefined,
        });
      });

    this.gamification.achievementUnlocked$
      .pipe(untilDestroyed(this))
      .subscribe((a) => {
        if (!a) return;
        this.push({
          kind: 'achievement',
          icon: 'award',
          name: a.name,
          points: a.rewardPoints,
        });
      });

    this.gamification.challengeCompleted$
      .pipe(untilDestroyed(this))
      .subscribe((c) => {
        if (!c) return;
        this.push({
          kind: 'challenge',
          icon: 'target',
          name: c.name,
          points: c.rewardPoints,
        });
      });
  }

  dismiss(id: number): void {
    const toast = this.toasts.find((t) => t.id === id);
    if (!toast) return;
    toast.leaving = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.toasts = this.toasts.filter((t) => t.id !== id);
      this.cdr.markForCheck();
    }, 200);
  }

  trackById = (_: number, t: GamiToast): number => t.id;

  private push(partial: Omit<GamiToast, 'id'>): void {
    const toast: GamiToast = { ...partial, id: ++this.seq };
    // Newest on top; cap the stack so a burst of events can't fill the screen.
    this.toasts = [toast, ...this.toasts].slice(0, 4);
    this.cdr.markForCheck();
    setTimeout(() => this.dismiss(toast.id), this.DURATION);
  }
}
